import React, { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import {
  File,
  Image,
  Video,
  Music,
  Archive,
  FileText,
  Download,
  Eye,
  Loader2,
  X,
  Upload,
  Edit
} from 'lucide-react'
import { apiRequest } from '@/services/api'
import { useAuth } from '@/context/AuthContext'
import { USER_ROLES, API_BASE_URL, API_PREFIX } from '@/utils/constants'
import toast from 'react-hot-toast'

// Helper function to get file accept types
const getFileAcceptTypes = (dataType) => {
  switch (dataType) {
    case 'Image': return 'image/*'
    case 'Video': return 'video/*'
    case 'Audio': return 'audio/*'
    case 'Document': return '.pdf,.doc,.docx,.txt,.rtf'
    case 'Archive': return '.zip,.rar,.7z,.tar,.gz'
    case 'File':
    default: return '*/*'
  }
}

const FileDisplayCell = ({
  fileId,
  dataType,
  isEditing = false,
  onStartEdit,
  onSave,
  onCancel
}) => {
  const { user } = useAuth()
  const [isUploading, setIsUploading] = useState(false)

  // Fetch file details by filename instead of ID
  const { data: fileData, isLoading, error } = useQuery({
    queryKey: ['file-by-filename', fileId],
    queryFn: async () => {
      if (!fileId) return null
      // Try to get file by filename first
      try {
        const response = await apiRequest.get(`/files/by-filename/${fileId}`)
        return response.data
      } catch (error) {
        // Fallback: if it's a numeric ID, try the old way
        if (!isNaN(fileId)) {
          const response = await apiRequest.get(`/files/${fileId}`)
          return response.data
        }
        throw error
      }
    },
    enabled: !!fileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes,
    retry: false, // Don't retry failed requests to avoid multiple toast messages
    onError: () => {
      // Suppress error handling for file not found - this is expected behavior
      // The component will handle the error state by showing "File not found" UI
    }
  })

  // Get icon based on data type
  const getFileIcon = () => {
    switch (dataType) {
      case 'Image':
        return Image
      case 'Video':
        return Video
      case 'Audio':
        return Music
      case 'Document':
        return FileText
      case 'Archive':
        return Archive
      case 'File':
      default:
        return File
    }
  }

  // Handle image preview
  const handleImagePreview = async () => {
    try {
      const endpoint = !isNaN(fileId)
        ? `/files/${fileId}/download?inline=true`
        : `/files/by-filename/${fileId}/download?inline=true`

      const response = await apiRequest.get(endpoint, {
        responseType: 'blob'
      })

      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: fileData?.content_type || 'image/jpeg' })
      const url = window.URL.createObjectURL(blob)
      const newWindow = window.open(url, '_blank')

      // Clean up the blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 1000)

      if (!newWindow) {
        toast.error('Please allow popups to preview images')
      }
    } catch (error) {
      console.error('Preview failed:', error)
      toast.error('Failed to preview image')
    }
  }

  // Handle file download
  const handleDownload = async (e) => {
    e.stopPropagation()

    // Check permissions
    const canDownload = user?.role === USER_ROLES.ADMIN ||
                       user?.role === USER_ROLES.MANAGER ||
                       user?.role === USER_ROLES.AREA_MANAGER

    if (!canDownload) {
      toast.error('You do not have permission to download files')
      return
    }

    try {
      // Use filename-based download if we have a filename, otherwise fallback to ID
      const downloadUrl = !isNaN(fileId)
        ? `/files/${fileId}/download`
        : `/files/by-filename/${fileId}/download`

      const response = await apiRequest.get(downloadUrl, {
        responseType: 'blob',
      })

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileData?.original_filename || 'file')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('File downloaded successfully')
    } catch (error) {
      console.error('Error downloading file:', error)
      toast.error('Failed to download file')
    }
  }

  // Handle file upload for editing
  const handleFileUpload = async (file) => {
    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('is_public', 'true')
      formData.append('generate_thumbnail', 'true')

      const response = await apiRequest.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const newUniqueFilename = response.data.file.filename

      // Call onSave with the new unique filename
      if (onSave) {
        await onSave(newUniqueFilename)
        toast.success(`${file.name} uploaded successfully`)
      }

    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    } finally {
      setIsUploading(false)
    }
  }

  // Handle file preview
  const handlePreview = (e) => {
    e.stopPropagation()

    // Check permissions
    const canPreview = user?.role === USER_ROLES.ADMIN ||
                      user?.role === USER_ROLES.MANAGER ||
                      user?.role === USER_ROLES.AREA_MANAGER

    if (!canPreview) {
      toast.error('You do not have permission to preview files')
      return
    }

    // For images, create a blob URL and open in new tab
    if (dataType === 'Image') {
      handleImagePreview()
    } else {
      // For other file types, trigger download
      handleDownload(e)
    }
  }

  // Get file accept types based on data type
  const getFileAcceptTypes = () => {
    switch (dataType) {
      case 'Image':
        return 'image/*'
      case 'Video':
        return 'video/*'
      case 'Audio':
        return 'audio/*'
      case 'Document':
        return '.pdf,.doc,.docx,.txt,.rtf'
      case 'Archive':
        return '.zip,.rar,.7z,.tar,.gz'
      case 'File':
      default:
        return '*/*'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    )
  }

  if (error || !fileData) {
    // Show editable state for file not found if user has edit permissions
    const canEdit = user?.role === USER_ROLES.ADMIN ||
                   user?.role === USER_ROLES.MANAGER ||
                   user?.role === USER_ROLES.AREA_MANAGER

    if (isEditing && canEdit) {
      return (
        <div className="space-y-2">
          {/* File upload */}
          <div className="flex items-center space-x-2">
            <input
              type="file"
              accept={getFileAcceptTypes(dataType)}
              onChange={(e) => {
                const file = e.target.files[0]
                if (file) {
                  handleFileUpload(file)
                }
              }}
              disabled={isUploading}
              className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />

            {isUploading && (
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            )}

            <button
              onClick={onCancel}
              disabled={isUploading}
              className="px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {isUploading && (
            <div className="text-xs text-blue-600">
              Uploading file...
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
          <File className="h-4 w-4" />
          <span className="text-sm">File not found</span>
        </div>
        {canEdit && onStartEdit && (
          <button
            onClick={onStartEdit}
            className="ml-2 p-1 text-gray-400 hover:text-gray-600"
            title="Upload replacement file"
          >
            <Edit className="h-4 w-4" />
          </button>
        )}
      </div>
    )
  }

  const Icon = getFileIcon()
  const canInteract = user?.role === USER_ROLES.ADMIN ||
                     user?.role === USER_ROLES.MANAGER ||
                     user?.role === USER_ROLES.AREA_MANAGER

  // Show file upload interface when editing
  if (isEditing) {
    return (
      <div className="space-y-2">
        {/* Current file info */}
        {fileData && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <Icon className="h-3 w-3" />
            <span className="truncate">Current: {fileData.original_filename}</span>
          </div>
        )}

        {/* File upload */}
        <div className="flex items-center space-x-2">
          <input
            type="file"
            accept={getFileAcceptTypes()}
            onChange={(e) => {
              const file = e.target.files[0]
              if (file) {
                handleFileUpload(file)
              }
            }}
            disabled={isUploading}
            className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />

          {isUploading && (
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          )}

          <button
            onClick={onCancel}
            disabled={isUploading}
            className="px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>

        {isUploading && (
          <div className="text-xs text-blue-600">
            Uploading file...
          </div>
        )}
      </div>
    )
  }

  // Show file display with edit option
  return (
    <>
      <div
        className="flex items-center space-x-2 group cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors"
        onClick={onStartEdit}
      >
        <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="truncate text-sm text-gray-900">
            {fileData.original_filename}
          </div>
          <div className="text-xs text-gray-500">
            {(fileData.file_size / 1024).toFixed(1)} KB
          </div>
        </div>
        
        {canInteract && (
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {dataType === 'Image' && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handlePreview(e)
                }}
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                title="Preview"
              >
                <Eye className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDownload(e)
              }}
              className="p-1 text-gray-400 hover:text-green-600 transition-colors"
              title="Download"
            >
              <Download className="h-3 w-3" />
            </button>
            {onStartEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onStartEdit()
                }}
                className="p-1 text-gray-400 hover:text-orange-600 transition-colors"
                title="Replace file"
              >
                <Upload className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>


    </>
  )
}

export default FileDisplayCell
